"use client";

import React from "react";
import { SectionCard } from "./SectionCard";
import { FileDropzone } from "./FileDropzone";
import { FileBuckets, UploadStateMap } from "./types";
import { submissionContent, uploadAccepts } from "./content";

export function UploadsSection({
  fileBuckets,
  setBucket,
  uploadState,
  onRemoveFile,
  disablePicker,
  invalidBuckets = [],
  onLimitExceeded,
}: {
  fileBuckets: FileBuckets;
  setBucket: (key: string, files: File[]) => void;
  uploadState: UploadStateMap;
  onRemoveFile: (key: string, idx: number) => void;
  disablePicker?: boolean;
  invalidBuckets?: string[];
  onLimitExceeded?: (title: string, message: string) => void;
}) {
  const items = submissionContent.uploads.items;

  return (
    <SectionCard
      title="Files and Documents Uploads"
      subtitle="Please upload professional, high-resolution files. Max size support is 30 MB per section and max number of files is 10 per section. Low resolution is NOT accepted."
    >
      <FileDropzone
        name="product_pictures"
        label={items[0]?.label ?? "1. Product Pictures"}
        helper={items[0]?.hint}
        required
        accept={uploadAccepts.productPictures}
        files={fileBuckets.product_pictures || []}
        setFiles={(n) => setBucket("product_pictures", n)}
        uploadEntries={uploadState.product_pictures || []}
        onRemoveFile={(idx) => onRemoveFile("product_pictures", idx)}
        disablePicker={disablePicker}
        isInvalid={invalidBuckets.includes("product_pictures")}
        maxFiles={10}
        maxTotalBytes={30 * 1024 * 1024}
        onLimitExceeded={onLimitExceeded}
      />

      <FileDropzone
        name="product_3d_files"
        label={items[1]?.label ?? "2. Product 3D drawing files"}
        helper={items[1]?.hint}
        required
        accept={uploadAccepts.product3dFiles}
        files={fileBuckets.product_3d_files || []}
        setFiles={(n) => setBucket("product_3d_files", n)}
        uploadEntries={uploadState.product_3d_files || []}
        onRemoveFile={(idx) => onRemoveFile("product_3d_files", idx)}
        disablePicker={disablePicker}
        isInvalid={invalidBuckets.includes("product_3d_files")}
        maxFiles={10}
        maxTotalBytes={30 * 1024 * 1024}
        onLimitExceeded={onLimitExceeded}
      />

      <FileDropzone
        name="user_manual"
        label={items[2]?.label ?? '3. User Manual "English, Chinese and other available language"'}
        helper={items[2]?.hint}
        required
        accept={uploadAccepts.userManual}
        files={fileBuckets.user_manual || []}
        setFiles={(n) => setBucket("user_manual", n)}
        uploadEntries={uploadState.user_manual || []}
        onRemoveFile={(idx) => onRemoveFile("user_manual", idx)}
        disablePicker={disablePicker}
        isInvalid={invalidBuckets.includes("user_manual")}
        maxFiles={10}
        maxTotalBytes={30 * 1024 * 1024}
        onLimitExceeded={onLimitExceeded}
      />

      <FileDropzone
        name="certificates"
        label={items[3]?.label ?? "4. All Available Certificates"}
        helper={items[3]?.hint}
        required
        accept={uploadAccepts.certificates}
        files={fileBuckets.certificates || []}
        setFiles={(n) => setBucket("certificates", n)}
        uploadEntries={uploadState.certificates || []}
        onRemoveFile={(idx) => onRemoveFile("certificates", idx)}
        disablePicker={disablePicker}
        isInvalid={invalidBuckets.includes("certificates")}
        maxFiles={10}
        maxTotalBytes={30 * 1024 * 1024}
        onLimitExceeded={onLimitExceeded}
      />

      <FileDropzone
        name="packaging_diecut_artwork"
        label={items[4]?.label ?? "5. Retail Packaging Die-cut and Artwork files"}
        helper={items[4]?.hint}
        required
        accept={uploadAccepts.packagingDiecutArtwork}
        files={fileBuckets.packaging_diecut_artwork || []}
        setFiles={(n) => setBucket("packaging_diecut_artwork", n)}
        uploadEntries={uploadState.packaging_diecut_artwork || []}
        onRemoveFile={(idx) => onRemoveFile("packaging_diecut_artwork", idx)}
        disablePicker={disablePicker}
        isInvalid={invalidBuckets.includes("packaging_diecut_artwork")}
        maxFiles={10}
        maxTotalBytes={30 * 1024 * 1024}
        onLimitExceeded={onLimitExceeded}
      />

      <FileDropzone
        name="outline_images"
        label={items[5]?.label ?? "6. Outline image of the product from all sides"}
        helper={items[5]?.hint}
        required
        accept={uploadAccepts.outlineImages}
        files={fileBuckets.outline_images || []}
        setFiles={(n) => setBucket("outline_images", n)}
        uploadEntries={uploadState.outline_images || []}
        onRemoveFile={(idx) => onRemoveFile("outline_images", idx)}
        disablePicker={disablePicker}
        isInvalid={invalidBuckets.includes("outline_images")}
        maxFiles={10}
        maxTotalBytes={30 * 1024 * 1024}
        onLimitExceeded={onLimitExceeded}
      />

      <FileDropzone
        name="price_structure"
        label={items[6]?.label ?? "7. Price Structure"}
        helper={items[6]?.hint}
        required
        accept={uploadAccepts.priceStructure}
        files={fileBuckets.price_structure || []}
        setFiles={(n) => setBucket("price_structure", n)}
        uploadEntries={uploadState.price_structure || []}
        onRemoveFile={(idx) => onRemoveFile("price_structure", idx)}
        disablePicker={disablePicker}
        isInvalid={invalidBuckets.includes("price_structure")}
        maxFiles={10}
        maxTotalBytes={30 * 1024 * 1024}
        onLimitExceeded={onLimitExceeded}
      />

      <FileDropzone
        name="product_video"
        label={items[7]?.label ?? "8. Product Supporting Files"}
        helper={items[7]?.hint}
        required
        accept={uploadAccepts.productVideo}
        files={fileBuckets.product_video || []}
        setFiles={(n) => setBucket("product_video", n)}
        uploadEntries={uploadState.product_video || []}
        onRemoveFile={(idx) => onRemoveFile("product_video", idx)}
        disablePicker={disablePicker}
        isInvalid={invalidBuckets.includes("product_video")}
        maxFiles={10}
        maxTotalBytes={30 * 1024 * 1024}
        onLimitExceeded={onLimitExceeded}
      />
    </SectionCard>
  );
}
